/**
 * pdfjs.ts — ROP-teams' one pdf.js entry point.
 *
 * Bank PCs have the built-in PDF viewer turned off by group policy
 * (AlwaysOpenPdfExternally), inside the Teams desktop app too, so a PDF in an
 * <iframe> shows as an empty grey box with no error (CHG-0097, the blank
 * Signature Card). We draw PDFs ourselves with pdf.js instead (PdfCanvasViewer),
 * the fix the RCP kiosk made for BACKLOG-1539 (apps/RCP-portal/src/services/pdfjs.ts).
 *
 * No CDN: `pdfjs-dist` is bundled and the worker is imported with Vite's `?url`,
 * so it is a hashed same-origin asset allowed by `script-src 'self'`.
 * Modern build: ROP-teams runs only in the Teams desktop and iOS apps (current
 * WebView2 / WKWebView), unlike the customer-facing kiosk, which takes legacy/.
 */

type PdfJsModule = typeof import('pdfjs-dist');

export type PdfDocumentProxy = import('pdfjs-dist').PDFDocumentProxy;
export type PdfPageProxy = import('pdfjs-dist').PDFPageProxy;

let modulePromise: Promise<PdfJsModule> | null = null;

/**
 * Lazily import pdf.js (kept off the critical path) and point it at the bundled
 * worker, once. A failed load is not cached, so a chunk-load error can be retried.
 */
export async function loadPdfJs(): Promise<PdfJsModule> {
  if (!modulePromise) {
    const attempt = (async (): Promise<PdfJsModule> => {
      const pdfjs = await import('pdfjs-dist');
      const workerUrl: string = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      return pdfjs;
    })();
    attempt.catch(() => {
      modulePromise = null;
    });
    modulePromise = attempt;
  }
  return modulePromise;
}

/** Decode a `data:…;base64,…` URL to bytes without fetch() (CSP connect-src has no data:). */
export function dataUrlToBytes(dataUrl: string): Uint8Array | null {
  const match = /^data:[^;,]*(;[^,]*)?;base64,(.*)$/s.exec(dataUrl);
  if (!match) return null;
  try {
    const binary = atob(match[2] ?? '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}
